import { sizes, type Sizes } from '@smart/web-foundations';
import classnames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps } from '../../types';
import scss from './spinner.module.scss';

export type SpinnerProps = ExtendWithCommonProps<{
  /** Spinner's size. */
  size: Extract<Sizes, 'xs' | 'md' | 'xl' | '4xl'>;
  /** If true, spinner is loading. Renders children when `false`. */
  loading?: boolean;
  /** Child content. Rendered when loading is `false`. */
  children?: React.ReactNode;
}>;

/**
 * Used to indicate that content is loading.
 * If not loading it will render it's children.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-spinner--docs
 */
export const Spinner = React.forwardRef<React.ElementRef<'div'>, SpinnerProps>(
  ({ size = 'xl', className, children, loading = true, ...props }, ref) => {
    const id = React.useId();
    const classes = classnames(scss.spinner, scss[size], className);
    if (!loading) return children;
    return (
      <div
        className={classes}
        role="status"
        aria-live="polite"
        ref={ref}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 80 80"
          fill="none"
          className={scss.asset}
          height={sizes[size]}
          width={sizes[size]}
        >
          <g strokeWidth="8">
            <path d=" M 4 40 A 36 36 0 0 1 76 40" stroke={`url(#${id}-top)`} />
            <path
              d=" M 76 40 A 36 36 180 0 1 4 40"
              stroke={`url(#${id}-bottom)`}
            />
            <path
              d=" M 4 40 A 36 36 180 0 1 5 33"
              stroke="var(--ds-accent)"
              strokeLinecap="round"
            />
          </g>

          <defs>
            <linearGradient id={`${id}-top`}>
              <stop offset="0%" stopOpacity="0" stopColor="currentColor" />
              <stop offset="100%" stopOpacity="0.5" stopColor="currentColor" />
            </linearGradient>
            <linearGradient id={`${id}-bottom`}>
              <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
              <stop offset="100%" stopOpacity="0.5" stopColor="currentColor" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  },
);

Spinner.displayName = 'Spinner';
